/**
 * Installs a global fetch interceptor that attaches Rail Intel JWT session tokens to /api requests.
 */
const PUBLIC_API_PREFIXES = [
  "/api/users/validate-login",
  "/api/users/login",
  "/api/forgot-password",
  "/api/users/mfa/verify",
  "/api/users/mfa/setup/start",
  "/api/users/mfa/setup/confirm",
  "/api/users/mfa/email/send",
  "/api/users/mfa/email/verify",
  "/api/public/",
  "/api/health",
  "/api/stripe/webhook",
];

const TOKEN_KEY = "rv_session_token";
const EMAIL_KEY = "rv_session_email";

function isPublicApiUrl(url: string): boolean {
  try {
    const path = url.startsWith("http") ? new URL(url).pathname : url.split("?")[0];
    if (!path.startsWith("/api")) return false;
    return PUBLIC_API_PREFIXES.some((p) => path === p || path.startsWith(p));
  } catch {
    return false;
  }
}

let sessionToken: string | null = null;
let sessionEmail: string | null = null;
let installed = false;

function readStored(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionToken = sessionStorage.getItem(TOKEN_KEY);
    sessionEmail = sessionStorage.getItem(EMAIL_KEY);
  } catch {
    /* ignore */
  }
}

export function setSessionAuth(token: string | null, email: string | null): void {
  sessionToken = token;
  sessionEmail = email ? email.trim().toLowerCase() : null;
  try {
    if (token && sessionEmail) {
      sessionStorage.setItem(TOKEN_KEY, token);
      sessionStorage.setItem(EMAIL_KEY, sessionEmail);
    } else {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(EMAIL_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function clearSessionAuth(): void {
  setSessionAuth(null, null);
}

export function getSessionToken(): string | null {
  if (!sessionToken) readStored();
  return sessionToken;
}

export function getSessionEmail(): string | null {
  if (!sessionEmail) readStored();
  return sessionEmail;
}

/** No-op kept for older call sites. Session token is read from storage. */
export function setApiAuthInstance(_unused?: unknown): void {
  readStored();
}

/** Auth headers for non-fetch clients (e.g. XMLHttpRequest upload progress). */
export async function getApiAuthHeaders(
  existing?: Record<string, string>
): Promise<Record<string, string>> {
  const headers: Record<string, string> = { ...(existing || {}) };
  const token = getSessionToken();
  const email = getSessionEmail();
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (email) {
    const headerEmail = headers["X-User-Email"]?.trim();
    if (headerEmail && headerEmail.toLowerCase() !== email.toLowerCase()) {
      headers["X-View-As-User-Email"] = headerEmail;
    }
    headers["X-User-Email"] = email;
  }
  return headers;
}

export function installApiAuthInterceptor(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  readStored();
  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.pathname + input.search
          : input.url;

    const isApi = url.includes("/api/");
    if (isApi) {
      init = { credentials: "include", ...init };
    }

    if (!isPublicApiUrl(url) && isApi && getSessionToken()) {
      try {
        const headers = new Headers(init?.headers);
        if (!headers.has("Authorization")) {
          headers.set("Authorization", `Bearer ${getSessionToken()!}`);
        }
        const realEmail = getSessionEmail();
        if (realEmail) {
          const headerEmail = headers.get("X-User-Email")?.trim();
          if (headerEmail && headerEmail.toLowerCase() !== realEmail.toLowerCase()) {
            headers.set("X-View-As-User-Email", headerEmail);
          }
          headers.set("X-User-Email", realEmail);
        }
        init = { ...init, headers };
      } catch {
        /* proceed without token — server returns 401 */
      }
    }

    const method = String(
      init?.method || (input instanceof Request ? input.method : "GET") || "GET",
    ).toUpperCase();
    const retryableCall = isApi;
    const maxAttempts = !retryableCall ? 1 : method === "GET" || method === "HEAD" ? 4 : 2;

    let lastError: unknown;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const thisInput = input instanceof Request ? input.clone() : input;
        const res = await nativeFetch(thisInput, init);
        const gateway =
          res.status === 502 || res.status === 503 || res.status === 504;
        if (!retryableCall || !gateway || attempt === maxAttempts - 1) return res;
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
      } catch (err) {
        lastError = err;
        if (!retryableCall || attempt === maxAttempts - 1) throw err;
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
    throw lastError;
  };
}
